import React, { useMemo } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import { getPpsisWithFilter } from '../features/PpsiContainer/selectors';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import PpiForm from '../components/PpiForm';
import { getLanguage } from '../features/app/selectors';
import usePpi from '../features/PpiContainer/hooks/usePpi';
import useCreateDraftVersionPpi from '../features/PpiContainer/hooks/useCreateDraftVersionPpi';
import usePpsis from '../features/PpsiContainer/hooks/usePpsis';
import useAlert from '../features/alertSystem/hooks/useAlert';
import { ppiInProgressRoute } from '../routes';
import { Mode } from '../components/PpiForm/constants';
import PpsiBasicList from '../components/common/PpsiBasicList';
import { useUnpublishPpi } from '../features/PpiContainer/hooks/usePpiAction';
import View from './views';
import { States } from '../features/PpsiContainer/constants';
import useGlobalPpis from 'features/PpiContainer/hooks/useGlobalPpis';
import { toJS } from 'utils/immutableUtils';

function getData(ppi) {
  const data = _.cloneDeep(ppi.publishedPPIEnrichmentData);
  return {
    ...data,
    ppiId: ppi.id,
    itemCode: ppi.itemCode
  };
}

const PpiPublishedView = () => {
  const { id } = useParams();
  const history = useHistory();
  const [isFetchingGlobal, globalPpisRaw] = useGlobalPpis({
    status: States.PUBLISHED
  });

  const globalPpis = toJS(globalPpisRaw, []);
  const [ppi, isFetching] = usePpi(id);
  const [
    createDraftPpi,
    isCreatingDraft,
    isCreateDraftDone
  ] = useCreateDraftVersionPpi();
  const [isFetchingUsedInPpsis] = usePpsis({
    relatedToPpiId: id
  });
  const filter = useMemo(
    () => ({ relatedToPpiId: id, status: States.PUBLISHED }),
    [id]
  );
  const ppsisWithFilter = useSelector(getPpsisWithFilter(filter));
  const [unpublishPpi, isUnpublishing] = useUnpublishPpi();
  const [openAlert, closeAlert] = useAlert();
  const activeLanguage = useSelector(getLanguage);

  if (
    isFetching ||
    isCreatingDraft ||
    isFetchingUsedInPpsis ||
    isFetchingGlobal
  ) {
    return <ProgressFullscreen open />;
  }
  if (isCreateDraftDone) {
    return <Redirect to={ppiInProgressRoute(id)} />;
  }

  function showUsedInPpsiAlert() {
    openAlert({
      title: <FormattedMessage id="ppi.confirmCreateDraftInUseTitle" />,
      content: (
        <>
          <FormattedMessage
            id="ppi.confirmCreateDraftInUseMessage"
            values={{ br: <br /> }}
            key="message"
          />
          <PpsiBasicList ppsis={ppsisWithFilter} key="whereused" />
          {/* {(getData(ppi).upToDate === false)&&
            <FormattedMessage
            id="ppi.updateMsg"
            values={{ br: <br /> }}
            key="updateMsg"
          />
          } */}
        </>
      ),
      actions: {
        right: (
          <>
            <Button key="cancel" onClick={closeAlert} color="primary">
              <FormattedMessage id="ppi.confirmCreateDraftInUseCancel" />
            </Button>
            <Button
              key="ok"
              onClick={() => {
                closeAlert();
                createDraftPpi(id);
              }}
              color="secondary"
            >
              <FormattedMessage id="ppi.confirmCreateDraftInUseOk" />
            </Button>
          </>
        )
      }
    });
  }

  function handleEditInstruction() {
    const hasInProgressVersion = !!ppi.inProgressPPIEnrichmentData;

    if (hasInProgressVersion) {
      history.push(ppiInProgressRoute(id));
    } else if (ppsisWithFilter.size > 0) {
      showUsedInPpsiAlert();
    } else {
      createDraftPpi(id);
    }
  }

  function handleUnpublishPpi() {
    unpublishPpi(id);
  }

  View.PpiPublished.visit(ppi, activeLanguage);
  return (
    <PpiForm
      data={getData(ppi)}
      relationshipId={ppi && ppi.relationshipId}
      dataLabels={{
        title: (data, { language }) =>
          _.get(data, `info.${language}.presentationName`),
        status: data => data.enrichmentStatus,
        modifiedBy: 'lastModifiedBy',
        modifiedDate: 'modifyDate'
      }}
      showProgress={isUnpublishing}
      mode={Mode.READ}
      onCreateDraft={handleEditInstruction}
      onUnpublish={handleUnpublishPpi}
      completePpiData={ppi}
      globalPpis={globalPpis}
    />
  );
};

export default PpiPublishedView;
